/**
 * Où se pose chaque carte de la vue « Hiérarchie » (ticket 35) : un arbre horizontal, le
 * dossier à gauche, une colonne par niveau déplié, les sœurs de haut en bas dans l'ordre
 * que le serveur a donné (ton rang, puis l'arrivée).
 *
 * **Rien n'est stocké** (AGENTS, interdit n° 2) : tout se recalcule à chaque rendu, à partir
 * de l'ordre, de ce qui est déplié et de la hauteur que chaque carte a prise à l'écran. La
 * hauteur vient du DOM parce qu'un titre long passe sur plusieurs lignes : il se lit en
 * entier, et la colonne s'allonge d'autant au lieu de le couper.
 *
 * Une carte repliée prend la ligne suivante ; une carte dépliée se centre sur le bloc de ses
 * bulles, sans jamais remonter sur la carte d'au-dessus.
 */

/** La largeur d'une carte, et celle d'une colonne : la différence est la place des traits. */
export const LARGEUR_CENTRE = 250;
export const LARGEUR_CARTE = 260;
export const COLONNE = 310;
/** L'écart entre deux cartes d'une même colonne. */
export const INTERLIGNE = 16;
/** La hauteur qu'on suppose à une carte avant de l'avoir mesurée. */
const HAUTEUR_SUPPOSEE = 46;

export interface NoeudAPlacer {
  id: string;
  /** Les bulles dedans, dans l'ordre de la vue. Vide : rien dedans, ou pas encore lu. */
  enfants: NoeudAPlacer[];
  /** Déplié : ses bulles sont posées dans la colonne suivante. */
  deplie: boolean;
}

export interface PlaceDeCarte {
  /** Le coin haut gauche. */
  x: number;
  y: number;
  largeur: number;
  /** Le niveau : 0 pour le dossier ou la bulle dont c'est la feuille. */
  niveau: number;
}

export interface Trait {
  /** Pour la clé de rendu : la bulle d'arrivée. */
  vers: string;
  de: { x: number; y: number };
  a: { x: number; y: number };
}

export interface Hierarchie {
  places: Map<string, PlaceDeCarte>;
  traits: Trait[];
  largeur: number;
  hauteur: number;
}

export function placerLaHierarchie(
  racine: NoeudAPlacer,
  hauteurs: ReadonlyMap<string, number>
): Hierarchie {
  const places = new Map<string, PlaceDeCarte>();
  const traits: Trait[] = [];
  let curseur = 0;
  let niveauMax = 0;

  function poser(noeud: NoeudAPlacer, niveau: number): number {
    niveauMax = Math.max(niveauMax, niveau);
    const largeur = niveau === 0 ? LARGEUR_CENTRE : LARGEUR_CARTE;
    const hauteur = hauteurs.get(noeud.id) ?? HAUTEUR_SUPPOSEE;
    const x = niveau * COLONNE;
    const debut = curseur;
    let milieu: number;

    if (noeud.deplie && noeud.enfants.length > 0) {
      const milieux = noeud.enfants.map((enfant) => poser(enfant, niveau + 1));
      milieu = (milieux[0]! + milieux[milieux.length - 1]!) / 2;
      // Plus haute que le bloc de ses bulles : elle ne remonte pas sur la carte d'au-dessus,
      // et la suivante ne remonte pas sur elle.
      milieu = Math.max(milieu, debut + hauteur / 2);
      curseur = Math.max(curseur, milieu + hauteur / 2 + INTERLIGNE);
      noeud.enfants.forEach((enfant, i) => {
        traits.push({
          vers: enfant.id,
          de: { x: x + largeur, y: milieu },
          a: { x: (niveau + 1) * COLONNE, y: milieux[i]! },
        });
      });
    } else {
      milieu = curseur + hauteur / 2;
      curseur += hauteur + INTERLIGNE;
    }

    places.set(noeud.id, { x, y: milieu - hauteur / 2, largeur, niveau });
    return milieu;
  }

  poser(racine, 0);

  return {
    places,
    traits,
    largeur: niveauMax * COLONNE + (niveauMax === 0 ? LARGEUR_CENTRE : LARGEUR_CARTE),
    hauteur: Math.max(0, curseur - INTERLIGNE),
  };
}

/** Un trait courbe, d'un bord droit au bord gauche de la colonne suivante. */
export function courbeHorizontale(de: { x: number; y: number }, a: { x: number; y: number }) {
  const milieu = (de.x + a.x) / 2;
  return `M${de.x} ${de.y} C${milieu} ${de.y} ${milieu} ${a.y} ${a.x} ${a.y}`;
}
